import { wrappedInputPropsAdapter } from "../../input";
import { Box } from "../../box";
import { ButtonPresets } from "./ButtonPresets";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { InputGroup, useInputGroupProps } from "../../input-group";
import { MenuPresets } from "./MenuPresets";
import { augmentElement, cssModule, isNil, mergeClasses, mergeProps, useControllableState, useEventCallback } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { TextInput } from "../../text-input";
import { areEqualDates, toMidnightDate } from "./dateUtils";
import { useDateInput } from "./useDateInput";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "input";
const Input = /*#__PURE__*/forwardRef((props, ref) => {
  const [inputGroupProps, isInGroup] = useInputGroupProps();
  const {
    className,
    max,
    min,
    onChange,
    onDateChange,
    style,
    value,
    wrapperProps,
    ...rest
  } = mergeProps(props, wrappedInputPropsAdapter(inputGroupProps));
  const dateProps = useDateInput({
    forwardedRef: ref,
    max,
    min,
    onChange,
    onDateChange,
    value
  });
  return /*#__PURE__*/_jsx(TextInput, { ...mergeProps(rest, {
      wrapperProps: mergeProps(wrapperProps !== null && wrapperProps !== void 0 ? wrapperProps : {}, {
        className: mergeClasses(className, cssModule("o-ui-date-input", isInGroup && "in-group")),
        style
      })
    }, dateProps)
  });
});
export function InnerDateInput({
  as,
  defaultValue,
  disabled,
  fluid,
  forwardedRef,
  onDateChange,
  placeholder = "dd/mm/yyyy",
  presets,
  presetsVariant = "compact",
  readOnly,
  value: valueProp,
  wrapperProps,
  ...rest
}) {
  const fluidValue = useResponsiveValue(fluid);
  const [value, setValue] = useControllableState(valueProp, defaultValue, null);
  const containerRef = useRef();
  const inputRef = useRef();
  useImperativeHandle(forwardedRef, () => {
    // For presets, used the group container as the ref element.
    if (!isNil(presets)) {
      const element = containerRef.current;

      element.focus = () => {
        var _inputRef$current;

        (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
      };

      return element;
    }

    return inputRef.current;
  });
  const applyDate = useCallback((event, newDate) => {
    if (!areEqualDates(value, newDate)) {
      setValue(newDate);

      if (!isNil(onDateChange)) {
        onDateChange(event, newDate);
      }
    }
  }, [onDateChange, value, setValue]);
  const handleDateChange = useEventCallback((event, newDate) => {
    applyDate(event, newDate);
  });
  const handleSelectPreset = useEventCallback((event, newIndex) => {
    const preset = presets[newIndex];

    if (!isNil(preset)) {
      applyDate(event, preset.date);
    }
  });
  const presetsProps = useMemo(() => {
    if (!isNil(presets)) {
      const selectedIndex = presets.findIndex(x => areEqualDates(toMidnightDate(x.date), toMidnightDate(value)));
      return {
        onSelectionChange: handleSelectPreset,
        selectedIndex: selectedIndex !== -1 ? selectedIndex : undefined,
        values: presets.map(x => x.text)
      };
    }

    return null;
  }, [presets, value, handleSelectPreset]);

  const inputMarkup = /*#__PURE__*/_jsx(Input, { ...mergeProps(rest, {
      onDateChange: handleDateChange,
      placeholder,
      ref: inputRef,
      value
    })
  });

  if (!isNil(presetsProps)) {
    return presetsVariant === "compact" ? /*#__PURE__*/_jsxs(InputGroup, { ...mergeProps({
        as,
        disabled,
        fluid: fluidValue,
        readOnly,
        ref: containerRef
      }, wrapperProps !== null && wrapperProps !== void 0 ? wrapperProps : {}),
      children: [inputMarkup, /*#__PURE__*/_jsx(MenuPresets, { ...presetsProps
      })]
    }) : /*#__PURE__*/_jsxs(Box, { ...mergeProps({
        as,
        className: cssModule("o-ui-date-input-button-presets", fluidValue && "fluid"),
        ref: containerRef
      }, wrapperProps !== null && wrapperProps !== void 0 ? wrapperProps : {}),
      children: [inputMarkup, !disabled && !readOnly && /*#__PURE__*/_jsx(ButtonPresets, { ...presetsProps
      })]
    });
  }

  return augmentElement(inputMarkup, {
    as,
    disabled,
    fluid: fluidValue,
    readOnly,
    wrapperProps
  });
}
InnerDateInput.defaultElement = DefaultElement;
export const DateInput = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerDateInput, { ...props,
  forwardedRef: ref
}));